import { useCallback, useMemo, useState } from "react";
import { LoadingShim, formatNumber } from "@narmi/design_system";
import { format } from "date-fns";

import {
  CursorCoordinatesPropType,
  VictoryArea,
  VictoryAxis,
  VictoryChart,
  VictoryCursorContainer,
  VictoryGroup,
  VictoryLine,
  VictoryScatter,
  VictoryTooltip
} from "victory";
import filters from "byzantine/src/filters";
import useElementWidth from "../../hooks/useElementWidth";

import styles from "./BaseChart.module.scss";
import {
  TICK_LABEL_PX,
  MIN_TICK_FONT_SIZE,
  MAX_TICK_FONT_SIZE,
  TICK_LABEL_LINE_HEIGHT,
  GRID_STROKE_PX,
  VALUE_AXIS_LABEL_WIDTH_PX
} from "./constants";
import { TimePeriod, ChartDatum, ChartDomain } from "./types";
import GridLines from "./GridLines";
import {
  getTickValues,
  getGridLinePositions,
  getNearestDatum,
  getTimestamp,
  formatDateTick,
  formatValueAxisTick
} from "./utils";

type BaseChartProps = {
  timePeriod: TimePeriod;
  chartData: ChartDatum[];
  windowStart: Date;
  windowEnd: Date;
  isLoading: boolean;
  isSmall: boolean;
  error: unknown;
  /** Renders a visible horizontal axis line instead of the default transparent one. */
  showAxisLine?: boolean;
  /** Renders a left-hand value axis with currency-formatted tick labels. */
  showValueAxis?: boolean;
  /**
   * Overrides the default per-timePeriod tick format: either a fixed date-fns
   * format string, or a function for formats that depend on neighboring ticks
   * (e.g. showing a year only where it changes).
   */
  tickFormat?:
    | string
    | ((
        tick: Date | number | string,
        index: number,
        ticks: (Date | number | string)[]
      ) => string | string[]);
  /**
   * Explicit tick positions, overriding the default evenly-spaced ticks.
   * Use for fixed-cadence data (e.g. one point per month) so ticks land on
   * the real data points instead of arbitrary evenly-spaced positions that
   * can fall within the same period as a neighboring tick.
   */
  tickValues?: Date[];
  /**
   * Reserves extra vertical room for tick labels that wrap onto a second
   * line (a `tickFormat` function returning a 2-element array for some
   * ticks, e.g. ["Jan", "2026"]).
   */
  multilineTickLabels?: boolean;
  /** Suppresses the floating tooltip on hover, keeping just the point marker. */
  hideTooltip?: boolean;
  /** Called whenever the hovered/active data point changes (null when nothing is hovered). */
  onActiveDatumChange?: (datum: ChartDatum | null) => void;
  /**
   * Overrides the default aspect-ratio-locked height with a fixed pixel
   * height that holds steady across container widths (converted internally
   * to the SVG's viewBox units). Use for callers whose container width
   * varies a lot (e.g. spans a variable number of dashboard columns), where
   * the default fixed aspect ratio would otherwise render taller or
   * shorter - including uncomfortably squished - purely as a side effect of
   * how wide the container happens to be.
   */
  chartHeightPx?: number;
  /**
   * Anchors the first tick's label to start at (instead of center on) its
   * tick position, and the last tick's label to end at its tick position,
   * so a label pinned exactly at the domain's edge doesn't clip against the
   * chart's boundary. Use when `tickValues` places a tick exactly at
   * `windowStart`/`windowEnd`, which the default evenly-spaced ticks don't
   * do (they're centered within slices, not pinned to the edges). Unlike
   * reserving extra chart padding, this keeps the plotted line/area
   * touching the actual edges of the chart.
   */
  anchorEdgeTickLabels?: boolean;
};

const BaseChart = ({
  timePeriod,
  chartData,
  windowStart,
  windowEnd,
  isLoading,
  isSmall,
  error,
  showAxisLine = false,
  showValueAxis = false,
  tickFormat,
  tickValues: tickValuesProp,
  multilineTickLabels = false,
  hideTooltip = false,
  onActiveDatumChange,
  chartHeightPx,
  anchorEdgeTickLabels = false
}: BaseChartProps) => {
  const [activeDatum, setActiveDatum] = useState<ChartDatum | null>(null);
  const [containerRef, containerWidth] = useElementWidth();
  // These dimensions reflect the aspect ratio to use for the chart's generated SVG.
  const defaultChartHeight = isSmall ? 300 : 222;
  const chartWidth = isSmall ? 720 : 1440;

  // Victory renders a responsive SVG (`viewBox` + `width: 100%`), so everything
  // inside is scaled by `containerWidth / chartWidth`. Multiplying by the
  // inverse converts a target pixel size into the user units that render at
  // that size. Falls back to 1:1 until the first measurement lands.
  const userUnitsPerPx = containerWidth > 0 ? chartWidth / containerWidth : 1;
  // chartHeightPx is a target that holds steady in actual pixels regardless
  // of container width, unlike the aspect-ratio-locked default above.
  const chartHeight =
    chartHeightPx !== undefined
      ? Math.round(chartHeightPx * userUnitsPerPx)
      : defaultChartHeight;
  const tickFontSize = Math.min(
    Math.max(TICK_LABEL_PX * userUnitsPerPx, MIN_TICK_FONT_SIZE),
    MAX_TICK_FONT_SIZE
  );
  // A second tspan line renders one tickFontSize below the first (Victory's
  // per-line `dy` for array-valued labels), so reserve one extra font-size
  // worth of room on top of the usual single-line allowance.
  const bottomPadding = Math.round(
    tickFontSize * (TICK_LABEL_LINE_HEIGHT + (multilineTickLabels ? 1 : 0))
  );
  const leftPadding = showValueAxis
    ? Math.round(VALUE_AXIS_LABEL_WIDTH_PX * userUnitsPerPx)
    : 0;
  const gridStrokeWidth = GRID_STROKE_PX * userUnitsPerPx;

  const formatTick = useCallback(
    (
      tick: Date | number | string,
      index: number,
      ticks: (Date | number | string)[]
    ) => {
      if (typeof tickFormat === "function") return tickFormat(tick, index, ticks);
      if (!tickFormat) return formatDateTick(tick, timePeriod);

      const timestamp = getTimestamp(tick);
      return timestamp === null ? "" : format(new Date(timestamp), tickFormat);
    },
    [tickFormat, timePeriod]
  );

  const chartDomain = useMemo<ChartDomain | undefined>(() => {
    if (chartData.length === 0) return undefined;

    const balances = chartData.map(({ y }) => y);
    const minY = Math.min(...balances);
    const maxY = Math.max(...balances);
    const yRange = maxY - minY;
    const yPadding =
      yRange === 0 ? Math.max(Math.abs(maxY) * 0.05, 1) : yRange * 0.1;

    return {
      x: [windowStart, windowEnd] as [Date, Date],
      y: [minY - yPadding, maxY + yPadding] as [number, number]
    };
  }, [chartData, windowStart, windowEnd]);

  const tickValues =
    tickValuesProp ??
    (isSmall ? getTickValues(chartDomain, 6) : getTickValues(chartDomain));
  const gridLinePositions = getGridLinePositions(
    chartHeight,
    bottomPadding,
    gridStrokeWidth
  );
  const onCursorChange = useCallback(
    (value: CursorCoordinatesPropType) => {
      const cursorX =
        value instanceof Date ||
        typeof value === "number" ||
        typeof value === "string"
          ? value
          : null;
      const nextDatum = getNearestDatum(chartData, cursorX);
      if (nextDatum === activeDatum) return;
      setActiveDatum(nextDatum);
      onActiveDatumChange?.(nextDatum);
    },
    [chartData, activeDatum, onActiveDatumChange]
  );

  if (error) return null;

  return (
    <LoadingShim isLoading={isLoading}>
      {chartData.length > 0 ? (
        <div ref={containerRef} className={styles.chart}>
          <svg className={styles.svgDefs}>
            <defs>
              <linearGradient
                id="line-area-gradient"
                x1="0%"
                y1="0%"
                x2="0%"
                y2="100%"
              >
                <stop offset="0%" className={styles.areaGradientStart} />
                <stop offset="100%" className={styles.areaGradientEnd} />
              </linearGradient>
              <linearGradient
                id="cursor-line-gradient"
                x1="0"
                y1="0"
                x2="0"
                y2={chartHeight}
                gradientUnits="userSpaceOnUse"
              >
                <stop offset="0%" className={styles.cursorGradientStart} />
                <stop offset="50%" className={styles.cursorGradientStart} />
                <stop offset="100%" className={styles.cursorGradientEnd} />
              </linearGradient>
            </defs>
          </svg>

          <VictoryChart
            // Forces a full remount when the plotted window changes. Victory
            // doesn't reliably recompute rendered tick label text when only
            // `domain`/`tickValues` change in place, even though the plotted
            // line and scale do update — so a stable key across re-renders
            // can leave stale axis labels on screen.
            key={`${windowStart.getTime()}-${windowEnd.getTime()}-${chartData.length}`}
            height={chartHeight}
            width={chartWidth}
            style={{
              background: {
                fill: "var(--base-chart-background-fill)"
              }
            }}
            padding={{ top: 40, bottom: bottomPadding, left: leftPadding, right: 0 }}
            domain={chartDomain}
            scale={{ x: "time" }}
            containerComponent={
              <VictoryCursorContainer
                portalZIndex={1}
                cursorDimension="x"
                // We use this component to track cursor location
                cursorComponent={<div />}
                onCursorChange={onCursorChange}
              />
            }
          >
            <VictoryAxis
              orientation="bottom"
              offsetY={bottomPadding}
              tickValues={tickValues}
              // Victory's types don't reflect that a tickFormat function may
              // return a string[] to render a multi-line label (it does, via
              // VictoryLabel), so the return type is narrowed here to match.
              tickFormat={
                formatTick as (
                  tick: Date | number | string,
                  index: number,
                  ticks: (Date | number | string)[]
                ) => string
              }
              style={{
                tickLabels: {
                  fontWeight: "var(--base-chart-axis-font-weight)",
                  // Centering the first/last label on a tick pinned exactly
                  // at the domain edge would clip it against the chart
                  // boundary, so anchor those two inward instead.
                  textAnchor: anchorEdgeTickLabels
                    ? ({
                        index,
                        ticks
                      }: {
                        index?: string | number;
                        ticks?: unknown[];
                      }) => {
                        if (index === 0) return "start";
                        if (index === (ticks?.length ?? 0) - 1) return "end";
                        return "middle";
                      }
                    : "middle",
                  fontSize: tickFontSize,
                  fontFamily: "var(--base-chart-axis-font-family)",
                  fill: "var(--base-chart-axis-color)"
                },
                axis: {
                  stroke: showAxisLine
                    ? "var(--base-chart-axis-line-color)"
                    : "var(--base-chart-axis-stroke)"
                }
              }}
            />
            {showValueAxis && (
              <VictoryAxis
                dependentAxis
                tickFormat={formatValueAxisTick}
                style={{
                  tickLabels: {
                    fontWeight: "var(--base-chart-axis-font-weight)",
                    fontSize: tickFontSize,
                    fontFamily: "var(--base-chart-axis-font-family)",
                    fill: "var(--base-chart-axis-color)"
                  },
                  axis: {
                    stroke: "var(--base-chart-axis-line-color)"
                  },
                  // GridLines already draws the horizontal gridlines.
                  grid: { stroke: "none" }
                }}
              />
            )}
            <VictoryGroup data={chartData} y={(datum) => datum.y}>
              <VictoryArea
                y0={() => chartDomain?.y[0] ?? 0}
                style={{ data: { fill: "url(#line-area-gradient)" } }}
                interpolation="catmullRom"
              />
              <VictoryLine
                style={{
                  data: {
                    stroke: "var(--base-chart-halo-stroke)",
                    strokeWidth: 9
                  }
                }}
                interpolation="catmullRom"
              />
              <VictoryLine
                style={{
                  data: {
                    stroke: "var(--base-chart-line-stroke)",
                    strokeWidth: 2
                  }
                }}
                interpolation="catmullRom"
              />
            </VictoryGroup>
            {activeDatum && chartDomain && (
              <VictoryLine
                data={[
                  { x: activeDatum.x, y: activeDatum.y },
                  { x: activeDatum.x, y: chartDomain.y[0] }
                ]}
                style={{
                  data: {
                    stroke: "url(#cursor-line-gradient)",
                    strokeWidth: 3,
                    strokeDasharray: "var(--base-chart-cursor-dasharray)",
                    strokeLinecap: "var(--base-chart-cursor-linecap)"
                  }
                }}
              />
            )}
            {activeDatum && (
              <VictoryScatter
                data={[activeDatum]}
                labels={({ datum }: { datum: ChartDatum }) => [
                  `${formatNumber(datum.y, "currency")}`,
                  filters.shortMonthDayYear(datum.x)
                ]}
                labelComponent={
                  <VictoryTooltip
                    active={!hideTooltip}
                    constrainToVisibleArea
                    orientation="top"
                    pointerOrientation="bottom"
                    pointerLength={0}
                    pointerWidth={0}
                    // Lifts tooltip above the scatter point
                    centerOffset={{ y: -20 }}
                    flyoutStyle={{
                      fill: "var(--base-chart-tooltip-fill)",
                      filter: "var(--base-chart-tooltip-shadow)"
                    }}
                    flyoutPadding={{
                      top: 6,
                      bottom: 6,
                      left: 10,
                      right: 10
                    }}
                    style={[
                      {
                        fill: "var(--base-chart-tooltip-label-color)",
                        textAnchor: "start",
                        fontSize: tickFontSize
                      },
                      {
                        fill: "var(--base-chart-tooltip-sublabel-color)",
                        textAnchor: "start",
                        fontSize: tickFontSize
                      }
                    ]}
                  />
                }
                size={4}
                style={{ data: { fill: "var(--base-chart-point-fill)" } }}
              />
            )}
            <GridLines
              linePositions={gridLinePositions}
              chartWidth={chartWidth}
              strokeWidth={gridStrokeWidth}
              xOffset={leftPadding}
            />
          </VictoryChart>
        </div>
      ) : (
        <div className={styles.emptyState} />
      )}
    </LoadingShim>
  );
};

export default BaseChart;
